import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Lock, Mail, Loader2, AlertCircle } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";

const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001") + "/api/admin";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      localStorage.setItem("ka_admin_token", data.token);
      localStorage.setItem("ka_admin_user", JSON.stringify(data.admin));
      
      toast.success("Welcome back, Commander");
      navigate("/admin");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#8B0000]/20 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(139,0,0,0.1)_0%,transparent_100%)]" />
      </div>

      <Card className="w-full max-w-md bg-[#1a1a1a]/90 border-[#8B0000]/30 backdrop-blur-xl relative z-10">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-[#8B0000]/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#8B0000]/40">
            <Shield className="w-8 h-8 text-[#8B0000]" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tighter text-white">ADMIN ACCESS</CardTitle>
          <p className="text-gray-500 text-sm">Secure terminal for Knight Arena management</p>
        </CardHeader>
        
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-6 bg-red-900/20 border-red-500/50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-400">Admin Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Email"
                  className="bg-black/40 border-[#8B0000]/30 pl-10 focus:border-[#8B0000] text-white"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" senior className="text-gray-400">Access Key</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Password"
                  className="bg-black/40 border-[#8B0000]/30 pl-10 focus:border-[#8B0000] text-white"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#8B0000] hover:bg-[#B22222] text-white h-11"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : "Authenticate"}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="justify-center border-t border-[#8B0000]/10 mt-4 pt-6">
          <p className="text-xs text-gray-600 font-mono uppercase tracking-widest">
            Level 4 clearance required
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
